import { useState, SetStateAction, Dispatch, useMemo } from "react";
import { toast } from "react-toastify";
import { useForm, useWatch } from "react-hook-form";
import { DialogContent, DialogTitle, Stack } from "@mui/material";

import { CommonDialog } from "../../../components/common/CommonDialog";
import { FormButtonGroup } from "../../../components/form";

import { useSessionContext } from "../../../contexts/SessionContext";
import { useValueSet } from "../../../hooks/useValueSet";
import ProfessionalForm from "../../professionalregister/components/ProfessionalForm";
import {
  GetPractitionerRole,
  UpdatePractitionerRole,
} from "../../../services/fhir";
import { currentFeatures, oids } from "../../../configs/Settings";
import { isMobile } from "react-device-detect";
import { usePractitionerRoleContext } from "../../../contexts/PractitionerRoleContext";
import {
  getCboValueSetId,
  getCboValueSetUrl,
} from "../../professionalregister/cboValueSetByConselho";

export interface IFormInput {
  classe: number | undefined;
  uf: string;
  speciality: string;
  number: string;
}

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  seletedRole: any;
  setSelectedRole: any;
}

export default function EditPractitionerRoleDialog({
  isOpen,
  setIsOpen,
  seletedRole,
  setSelectedRole,
}: IProps) {
  const { user } = useSessionContext();
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const classId = {
    value: seletedRole?.classId?.split("/")[1].slice(2),
    uf: seletedRole?.classId?.split("/")[1].slice(0, 2),
  };

  const defaultValues = {
    number: classId?.value,
    classe: seletedRole?.classId?.split("/")?.[0],
    uf: { label: classId.uf, value: classId.uf },
    speciality: currentFeatures.defaultApp
      ? seletedRole?.specialty?.map((el: any) => {
          return {
            value: el?.coding[0]?.code,
            label: `${el?.coding[0]?.display} - ${el?.coding[0]?.code}`,
          };
        })
      : seletedRole?.specialty?.[0]?.coding[0]?.display,
  };

  const methods = useForm({ defaultValues });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty, isValid },
    reset,
  } = methods;

  // Conselho selecionado — define ValueSet de CBO (r5) dinâmico
  const selectedClasse = useWatch({ control, name: "classe" });
  const conselhoKey =
    typeof selectedClasse === "string" ? selectedClasse : undefined;
  const cboValueSetId = getCboValueSetId(conselhoKey);
  const cboCustomUrl = getCboValueSetUrl(conselhoKey);

  // Hook para buscar especialidades do CBO do conselho (r5)
  const { options: specialtiesOptions, loading: specialtiesLoading } =
    useValueSet({
      valueSet: cboValueSetId,
      version: "r5",
      searchTerm: searchTerm,
      customUrl: cboCustomUrl,
    });

  // Formatar as opções para o formato esperado pelo componente
  const specialties = useMemo(() => {
    if (!cboCustomUrl) return [];
    return (
      specialtiesOptions?.map((item) => ({
        value: item.code,
        label: `${item.display} - ${item.code}`,
        display: item.display,
      })) ?? []
    );
  }, [specialtiesOptions, cboCustomUrl]);

  function handleClose() {
    setIsOpen(false);
    setSelectedRole();
    reset(defaultValues);
  }

  const onSubmit = async (data: any) => {
    setLoading(true);

    try {
      const practitionerRole = await GetPractitionerRole({
        id: `${seletedRole?.id.split("-")[0]}-${oids.cpf}-${user.username}`,
      });
      await UpdatePractitionerRole(practitionerRole, {
        conselho: data?.classe,
        numero: data?.number,
        uf: data?.uf,
        speciality: currentFeatures.defaultApp
          ? data?.speciality
          : [
              {
                value: "1",
                label: data?.speciality || "Veterinária",
                display: data?.speciality || "Veterinária",
              },
            ],
      });

      handleClose();
      toast.success("Registro profissional editado com sucesso!");
      window.location.reload();
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonDialog
      handleClose={handleClose}
      isOpen={isOpen}
      mobileView={isMobile}
    >
      <DialogTitle textAlign="center">
        Editar informações profissionais
      </DialogTitle>
      <DialogContent>
        <ProfessionalForm
          edit
          setSearchTerm={setSearchTerm}
          setValue={setValue}
          onNextButtonClick={handleSubmit(() => {})}
          control={control}
          specialties={specialties}
          loading={loading || specialtiesLoading}
        />

        <Stack
          minWidth="100%"
          mt={3}
          direction="row"
          justifyContent={"space-between"}
          spacing={2}
        >
          <FormButtonGroup
            onGoBackButtonClick={handleClose}
            onNextButtonClick={handleSubmit(onSubmit)}
            goBackButtonText="Cancelar"
            nextButtonText="Salvar"
            loading={loading}
            disabled={!isDirty || !isValid}
          />
        </Stack>
      </DialogContent>
    </CommonDialog>
  );
}
